import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import * as actions from "store/actions";
import * as Path from "Routes/paths";
import {
  getActivePackagesList,
  getPackageDetailsById,
  getPackageInvitationsList,
} from "store/services/packageService";
import { formatPrice } from "helpers/common";
import {
  getInstallmentBreakdown,
  packageMatchesReference,
} from "helpers/packagePayment";

const POLL_ATTEMPTS = 5;
const POLL_DELAY_MS = 3000;

const wait = (duration) =>
  new Promise((resolve) => {
    setTimeout(resolve, duration);
  });

const PackagePaymentSuccess = ({ dispatch, isAuthenticated }) => {
  const [packageDetails, setPackageDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState("Finalizing your package access...");
  const [isPackageReady, setIsPackageReady] = useState(false);

  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const paymentId = urlParams.get("payment_intent");
  const packageId = urlParams.get("package");
  const inviteId = urlParams.get("invite");
  const redirectStatus = urlParams.get("redirect_status");
  const installmentBreakdown = getInstallmentBreakdown(packageDetails);

  useEffect(() => {
    let isMounted = true;

    const loadPaymentStatus = async () => {
      try {
        dispatch(actions.persist_store({ loader: true }));

        let fetchedPackageDetails = null;
        if (packageId) {
          const response = await dispatch(getPackageDetailsById({ packageId }));
          fetchedPackageDetails = response?.body || null;
          if (isMounted) {
            setPackageDetails(fetchedPackageDetails);
          }
        }

        if (!isMounted) {
          return;
        }

        const packageReferences = [
          packageId,
          inviteId,
          fetchedPackageDetails?.id,
          fetchedPackageDetails?.package_invite_id,
          fetchedPackageDetails?.package_id,
        ];
        const hasPackageReferences = packageReferences.some(
          (reference) => reference !== null && typeof reference !== "undefined" && reference !== ""
        );

        const isInstallment = fetchedPackageDetails?.payment_type === "installment";
        setStatusMessage(
          isInstallment
            ? "Your installment plan has been initiated. We are waiting for the package status to refresh."
            : "Your payment was confirmed. We are refreshing your package status now."
        );

        let packageReady = false;

        if (!hasPackageReferences) {
          setStatusMessage(
            "Your payment was submitted. Package access may take a moment to update while we process it."
          );
        } else {
          for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
            const [activePackagesResponse, invitationResponse] = await Promise.all([
              getActivePackagesList(),
              dispatch(getPackageInvitationsList()),
            ]);

            if (!isMounted) {
              return;
            }

            const activePackages = activePackagesResponse?.body || [];
            const invitations = invitationResponse?.body || [];

            const isActive = activePackages.some((item) =>
              packageMatchesReference(item, packageReferences)
            );
            const isStillInvited = invitations.some((item) =>
              packageMatchesReference(item, packageReferences)
            );

            if (isActive || !isStillInvited) {
              packageReady = true;
              setIsPackageReady(true);
              setStatusMessage(
                isInstallment
                  ? "Your first installment is confirmed and your package access is being restored."
                  : "Your package purchase is complete and your access is ready."
              );
              break;
            }

            if (attempt < POLL_ATTEMPTS - 1) {
              await wait(POLL_DELAY_MS);
            }
          }
        }

        if (hasPackageReferences && isMounted) {
          if (!packageReady) {
            setStatusMessage(
              "Your payment has been submitted. Package access is still processing and should update shortly."
            );
          }
        }

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.log("Error loading package payment success", error);
        if (isMounted) {
          setStatusMessage(
            "Your payment was submitted. Package access may take a moment to update while we process it."
          );
          setIsLoading(false);
        }
      } finally {
        dispatch(actions.persist_store({ loader: false }));
      }
    };

    loadPaymentStatus();

    return () => {
      isMounted = false;
    };
  }, [dispatch, inviteId, packageId]);

  const purchaseSummary = packageDetails ? (
    <div
      className="commonCardBox p-3 mt-3"
      style={{ background: "#f6f6f6" }}
    >
      <h4 className="m-0 fw-sbold">{packageDetails.package_name}</h4>
      <p className="text-muted m-0 pt-2 is-multiline">{packageDetails.package_desc}</p>
      <p className="m-0 pt-2">
        <strong>Total:</strong> {packageDetails.currency_sign}{formatPrice(packageDetails.package_price)} ({packageDetails.currency_code})
      </p>
      {packageDetails.payment_type === "installment" && installmentBreakdown?.summary ? (
        <p className="m-0 pt-2">
          <strong>Installment plan:</strong> {installmentBreakdown.summary}
        </p>
      ) : null}
    </div>
  ) : null;

  return (
    <section
      className="w-100 position-relative d-flex align-items-center justify-content-center"
      style={{ background: "#f2f2f2", minHeight: "100vh" }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col className="my-2" lg="10">
            <div className="commenContent commonCardBox px-lg-4 p-3 bg-white">
              <div className="top py-2 bg-white">
                <h2 className="m-0">
                  {packageDetails?.payment_type === "installment"
                    ? "Purchase Initiated"
                    : "Payment Successful"}
                </h2>
                <p className="m-0 py-2">{statusMessage}</p>
                {redirectStatus ? (
                  <p className="m-0 py-1">
                    <strong>Payment status:</strong> {redirectStatus}
                  </p>
                ) : null}
                {paymentId ? (
                  <p className="m-0 py-1">
                    <strong>Payment ID:</strong> {paymentId}
                  </p>
                ) : null}
                {purchaseSummary}
                {isLoading || !isPackageReady ? (
                  <p className="m-0 pt-3">
                    Your package may take a brief moment to appear in your account.
                  </p>
                ) : (
                  <p className="m-0 pt-3">
                    Your package status has updated. You can safely return to your account.
                  </p>
                )}
                {isAuthenticated ? (
                  <Link to={`${Path.myAccount}?tab=MyPackage`} className="d-inline-block pt-3">
                    Return to My Packages
                  </Link>
                ) : null}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

const mapStateToProps = (state) => {
  return {
    isAuthenticated: state.Auth.isAuthenticated,
  };
};

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PackagePaymentSuccess);
